import { Layout } from "providers/Layout";
import { useMemo, useState } from "react";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Card } from "shared/components/Card";
import { Plus } from "untitledui-js-base";
import { AddIntegrationModal } from "views/settings-integration/components/AddIntegrationModal";
import { useSettingsController } from "views/settings-integration/controller";
import { PMSNames } from "./integrations/pms/constants";
import { IntegrationLogoIcon } from "views/settings-integration/components/IntegrationLogoIcon";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";

interface IntegrationsItem {
  enabled: boolean;
  title: string;
  description: string;
  link: string;
  icon?: ReactNode;
}

export default function IntegrationsView() {
  const [showAddIntegrationModal, setShowAddIntegrationModal] = useState(false);
  const navigate = useNavigate();
  const { partner, type } = useAuthContext();
  const { propertyManagementSystem, currentSettings, hubspotHubId } =
    useSettingsController();

  const integrations: IntegrationsItem[] = useMemo(() => {
    const pmsTitle = propertyManagementSystem
      ? PMSNames[propertyManagementSystem]
      : "PMS"; //this is fallback which should not happen, added just to be safe

    return [
      {
        enabled: Boolean(hubspotHubId),
        title: "HubSpot",
        description: "Integration between Boom and HubSpot",
        link: "/settings/integration/hubspot"
      },
      {
        enabled: Boolean(currentSettings.google.googleAnalyticsId),
        title: "Google Analytics",
        description: "Integration between Boom and Google Analytics",
        link: "/settings/integration/google-analytics"
      },
      {
        enabled: Boolean(propertyManagementSystem),
        title: pmsTitle,
        description: `Integration between Boom and ${pmsTitle}`,
        link: "/settings/integration/pms"
      },
      ...(type.isBoomCRM && Boolean(partner?.codebox_connected)
        ? [
            {
              enabled: true,
              title: "Codebox",
              description: "Integration with Codebox",
              link: "/settings/integration/codebox",
              icon: (
                <IntegrationLogoIcon id={IntegrationId.CODEBOX} size="BASE" />
              )
            }
          ]
        : []),
      ...(type.isBoomCRM && Boolean(partner?.zillow_connected)
        ? [
            {
              enabled: true,
              title: "Zillow",
              description: "Integration type: Zillow",
              link: "/settings/integration/zillow",
              icon: (
                <IntegrationLogoIcon id={IntegrationId.ZILLOW} size="BASE" />
              )
            }
          ]
        : []),
      ...(type.isBoomCRM && Boolean(partner?.apartments_com_connected)
        ? [
            {
              enabled: true,
              title: "Apartments.com",
              description: "Integration type: Apartments.com",
              link: "/settings/integration/apartments-com",
              icon: (
                <IntegrationLogoIcon
                  id={IntegrationId.APARTMENTS_COM}
                  size="BASE"
                />
              )
            }
          ]
        : [])
    ];
  }, [propertyManagementSystem, currentSettings, partner, type.isBoomCRM]);

  return (
    <Layout
      headerClassName="border-b print:border-none border-alpha-100 bg-white pt-[22px]"
      header={
        <>
          <div className="flex items-center justify-between border-b border-alpha-40 px-6 pb-[22px]">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Settings",
                  href: "/settings"
                },
                {
                  title: "Integration",
                  href: "/settings/integration"
                }
              ]}
            />
          </div>
          <div className="flex items-center justify-between p-6 pb-3">
            <h1 className="text-xl font-bold text-content-1000">Integration</h1>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              Icon={Plus}
              onClick={() => setShowAddIntegrationModal(true)}
            >
              Add integration
            </Button>
          </div>
        </>
      }
    >
      <div className="flex flex-wrap gap-4">
        {integrations.map((integration) => {
          if (!integration.enabled) return null;

          return (
            <Card
              className="flex flex-col p-8 w-[368px]"
              key={integration.title}
            >
              <div className="flex items-start justify-between">
                {integration.icon ?? <span />}
                <Badge variant={BadgeVariant.SUCCESS} size={BadgeSize.SM}>
                  Connected
                </Badge>
              </div>

              <div className="mt-6 mb-4 h-full">
                <div className="space-y-2">
                  <p className="font-bold text-base text-content-1000">
                    {integration.title}
                  </p>
                  <p className="text-sm text-content-400">
                    {integration.description}
                  </p>
                </div>
              </div>

              <div className="flex border-t border-alpha-100 pt-4 items-end">
                <Button
                  variant={ButtonVariant.TEXT}
                  size={ButtonSize.BASE}
                  onClick={() => navigate(integration.link)}
                >
                  Configure
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* We need this show to drop all states in modal when it closes */}
      <Show if={showAddIntegrationModal}>
        <AddIntegrationModal
          opened={showAddIntegrationModal}
          onClose={() => setShowAddIntegrationModal(false)}
        />
      </Show>
    </Layout>
  );
}
